/**
 * Standardized Skeleton Loader Components
 * 
 * These components provide consistent skeleton loading states that match
 * the actual content dimensions and layouts across the app.
 */
import React from 'react';

/**
 * Individual Channel Card Skeleton
 * Matches ChannelCard dimensions exactly
 */
export const ChannelCardSkeleton = ({ className = '' }) => (
  <div className={`relative w-full aspect-[2/3] bg-gray-700 rounded-lg overflow-hidden animate-pulse ${className}`} />
);

/**
 * Horizontal Rail Skeleton
 * Matches CategoryRail layout with responsive item counts
 * Note: Does not include title skeleton as CategoryRail handles its own title
 */
export const HorizontalRailSkeleton = ({ 
  isPosterFormat = false,
  className = '', 
}) => {
  // Match CategoryRail responsive breakpoints (RAIL_BREAKPOINTS). All cards are
  // portrait now, so poster and regular use the same denser counts.
  const getSkeletonCount = () => {
    if (typeof window === 'undefined') {return 7;} // SSR fallback

    const width = window.innerWidth;
    if (width <= 768) {return 3;} // mobile
    if (width <= 1024) {return 4;} // tablet
    if (width <= 1366) {return 6;} // small desktop
    return 7; // laptop and up
  };

  const skeletonCount = getSkeletonCount();

  return (
    <div className={className}>
      {/* Cards skeleton - matches Slider structure. skeletonCount mirrors the
          visible slide count, so flex-1 gives each card the same width a real
          slide gets at this viewport. */}
      <div className="flex overflow-hidden">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div key={index} className="flex-1 min-w-0 px-3">
            <ChannelCardSkeleton
              isPosterFormat={isPosterFormat}
              className="w-full"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Grid Skeleton
 * Matches CategoryPage and SearchResults grid layouts
 */
export const GridSkeleton = ({ 
  itemCount = 10, 
  className = '',
  showTitle = false, 
}) => (
  <div className={className}>
    {showTitle && (
      <div className="h-8 bg-gray-700 rounded w-48 mb-8 animate-pulse" />
    )}
    
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-6">
      {Array.from({ length: itemCount }).map((_, index) => (
        <div
          key={index}
          className="animate-pulse"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <div className="relative w-full aspect-[2/3] bg-gray-700 rounded-lg">
            {/* Shimmer effect */}
            <div
              className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent"
              style={{ 
                animation: 'shimmer 2s infinite',
                animationDelay: `${index * 100}ms`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Episode Carousel Skeleton
 * Matches EpisodeCarousel layout
 */
export const EpisodeCarouselSkeleton = ({ 
  slidesToShow = 4, 
  className = '', 
}) => (
  <div className={`episode-carousel-skeleton ${className}`}>
    <div className="flex space-x-4 overflow-hidden">
      {Array.from({ length: slidesToShow }).map((_, index) => (
        <div key={index} className="flex-shrink-0 animate-pulse" style={{ width: '200px' }}>
          {/* Episode thumbnail skeleton */}
          <div className="relative w-full aspect-video bg-gray-700 rounded-lg mb-2">
            <div
              className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent"
              style={{ 
                animation: 'shimmer 2s infinite',
                animationDelay: `${index * 100}ms`,
              }}
            />
          </div>
          
          {/* Episode title skeleton */}
          <div className="h-4 bg-gray-700 rounded w-3/4 mb-1" />
          
          {/* Episode description skeleton */}
          <div className="h-3 bg-gray-700 rounded w-full" />
          <div className="h-3 bg-gray-700 rounded w-2/3 mt-1" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Related Content Skeleton
 * Matches RelatedContent slider layout
 */
export const RelatedContentSkeleton = ({ className = '' }) => (
  <div className={`related-content-skeleton mb-8 ${className}`}>
    {/* Title skeleton */}
    <div className="h-6 bg-gray-700 rounded w-40 mb-4 animate-pulse" />
    
    {/* Items skeleton */}
    <div className="flex space-x-4 overflow-hidden">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex-shrink-0 animate-pulse" style={{ width: '160px' }}>
          {/* Thumbnail skeleton */}
          <div className="relative w-full aspect-[2/3] bg-gray-700 rounded-lg mb-2">
            <div
              className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent"
              style={{ 
                animation: 'shimmer 2s infinite',
                animationDelay: `${index * 100}ms`,
              }}
            />
          </div>
          
          {/* Title skeleton */}
          <div className="h-3 bg-gray-700 rounded w-full" />
          <div className="h-3 bg-gray-700 rounded w-2/3 mt-1" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Homepage Rails Skeleton
 * Shows multiple rail skeletons for homepage loading
 */
export const HomepageRailsSkeleton = ({ railCount = 5, className = '' }) => (
  <div className={className}>
    {Array.from({ length: railCount }).map((_, index) => (
      <div key={index} className="mb-8">
        {/* Title skeleton for each rail */}
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-700 rounded w-48 animate-pulse" />
          <div className="h-4 bg-gray-700 rounded w-16 animate-pulse" />
        </div>
        
        {/* Rail content skeleton */}
        <HorizontalRailSkeleton 
          isPosterFormat={index % 3 === 1} // Mix of regular and poster formats
        />
      </div>
    ))}
  </div>
);

// CSS for shimmer animation (to be added to global CSS)
export const skeletonStyles = `
@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}
`;

export default {
  ChannelCardSkeleton,
  HorizontalRailSkeleton,
  GridSkeleton,
  EpisodeCarouselSkeleton,
  RelatedContentSkeleton,
  HomepageRailsSkeleton,
  skeletonStyles,
};

